import { AUTHENTICATION_V2_ENDPOINT } from '@dtx-company/true-common/src/constants/endpoints'
import { ChangeEvent, useState } from 'react'
import { EntityTypes } from '@dtx-company/inter-app/src/redux/slices/modal'
import { MAX_FILE_SIZE, deleteProfileImage, uploadProfileImage } from './restRequests'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useMatchMutate } from '../../hooks/useMatchMutate'
import { useProfilePictureModal } from '@dtx-company/inter-app/src/hooks/useProfilePictureModal'
import events from '@dtx-company/inter-app/src/event-tracking/events/nav'

export interface ProfilePictureModalState {
  modalOpen: boolean
  setClose: () => void
  profileImage?: string
  imageFileString?: string
  error?: string
  fullName: string
  handleDeleteProfileImage: () => Promise<void>
  handleSaveProfileImage: () => Promise<void>
  handleUpload: (e: ChangeEvent<HTMLInputElement>) => void
  entityType?: EntityTypes
  submitting: boolean
  pictureType: 'profile' | 'team'
}

const getPath = (entityType: EntityTypes, entityId: string): string => {
  switch (entityType) {
    case EntityTypes.USER:
      return `${AUTHENTICATION_V2_ENDPOINT}/user`
    case EntityTypes.TEAM:
      return `&teamId=${entityId}`
    default:
      return ''
  }
}

export function useProfilePictureModalState(): ProfilePictureModalState {
  const { modalOpen, setClose, props } = useProfilePictureModal()
  const matchMutate = useMatchMutate()
  const [submitting, setSubmitting] = useState(false)
  const [imageFileString, setImageFileString] = useState<string | undefined>()
  const [error, setError] = useState<string | undefined>()
  const { jwt } = useAuthState()
  const entityId = props?.entityId
  const entityType = props?.entityType
  const entityName = props?.entityName || jwt?.ithacaId || 'avatar'
  const profileImage = props?.profilePicture
  const handleUpload = (e: ChangeEvent<HTMLInputElement>): void => {
    events.Platform_ProfilePictureUpload_Clicked_Button('Upload profile picture')
    const files = e?.target?.files
    if (files) {
      if (files[0]?.size > MAX_FILE_SIZE) {
        setError(`File is too large. Please upload a file less than ${MAX_FILE_SIZE / 1000000}MB.`)
      }
      setError('')
      const objectURL = files[0] ? URL.createObjectURL(files[0]) : undefined
      setImageFileString(objectURL)

      return
    }
    setImageFileString(undefined)
  }
  const handleSaveProfileImage = async (): Promise<void> => {
    events.Platform_ProfilePictureUpload_Clicked_Button('Save profile picture')
    if (imageFileString && entityId && entityType !== undefined && jwt?.token) {
      setSubmitting(true)
      await uploadProfileImage({
        image: imageFileString,
        token: jwt?.token,
        entityType,
        ...([EntityTypes.ORG, EntityTypes.TEAM].includes(entityType) && {
          entityId
        })
      })
      const pattern = new RegExp(getPath(entityType, entityId))
      await matchMutate({ pattern })
      setImageFileString('')
      setSubmitting(false)
      setClose()
    }
  }
  const handleDeleteProfileImage = async (): Promise<void> => {
    events.Platform_ProfilePictureUpload_Clicked_Button('Delete profile picture')
    if (imageFileString) {
      setImageFileString('')
      return
    }
    if (!profileImage) return
    if (entityId && entityType !== undefined && jwt?.token) {
      setSubmitting(true)
      await deleteProfileImage({
        token: jwt?.token,
        entityType,
        ...([EntityTypes.ORG, EntityTypes.TEAM].includes(entityType) && {
          entityId
        })
      })
      const pattern = new RegExp(entityId)
      await matchMutate({ pattern })
      setSubmitting(false)
      setClose()
    }
  }
  const pictureType = entityType === EntityTypes.USER ? 'profile' : 'team'

  return {
    modalOpen,
    handleSaveProfileImage,
    handleDeleteProfileImage,
    setClose: () => {
      setImageFileString('')
      setClose()
    },
    profileImage,
    imageFileString,
    error,
    fullName: entityName,
    handleUpload,
    entityType,
    submitting,
    pictureType
  }
}
